import React from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { Skeleton } from './Skeleton';

/**
 * What a page or a section shows while its first answer is on the way: the
 * shape of the real screen (tiles, rows, fields, bubbles) instead of a
 * spinner or a blank block. First load only — a screen that already has its
 * records keeps them while it refreshes.
 *
 * Every skeleton is a `role="status"` region that says "Loading…" to a screen
 * reader and fades in, so a fast answer never flashes.
 */

/* widths that vary from row to row, so a table reads as text rather than as bars */
const WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5', 'w-3/5'];
const widthAt = (row: number, col: number) => WIDTHS[(row * 3 + col * 5) % WIDTHS.length];
const card = 'rounded-2xl border border-border bg-surface shadow-soft';

export const SkeletonRegion: React.FC<{ kind: string; className?: string; children: React.ReactNode }> = ({ kind, className, children }) => (
  <div role="status" aria-busy="true" aria-live="polite" data-skeleton={kind} className={clsx('animate-fade-in', className)}>
    <span className="sr-only">Loading…</span>
    {children}
  </div>
);

/* ── lists ─────────────────────────────────────────────────────────────── */

export const ListToolbarSkeleton: React.FC<{ buttons?: number }> = ({ buttons = 2 }) => (
  <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-alt/40 px-4 py-3">
    <Skeleton className="h-9 w-full max-w-xs rounded-lg" />
    <div className="flex items-center gap-2">
      {Array.from({ length: buttons }, (_, i) => <Skeleton key={i} className={clsx('h-9 rounded-lg', i === buttons - 1 ? 'w-28' : 'w-9')} />)}
    </div>
  </div>
);

export const TableRowsSkeleton: React.FC<{ rows?: number; columns?: number; actions?: boolean }> = ({ rows = 8, columns = 5, actions = true }) => (
  <div>
    <div className="grid gap-4 border-b border-border bg-surface-alt/60 px-5 py-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))${actions ? ' 40px' : ''}` }}>
      {Array.from({ length: columns }, (_, c) => <Skeleton key={c} className={clsx('h-3', c === 0 ? 'w-24' : 'w-16')} />)}
      {actions && <span />}
    </div>
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="grid items-center gap-4 border-b border-border px-5 py-4 last:border-b-0" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))${actions ? ' 40px' : ''}` }}>
        {Array.from({ length: columns }, (_, c) => (
          c === 0
            ? <div key={c} className="flex items-center gap-3"><Skeleton className="h-9 w-9 shrink-0 rounded-lg" /><div className="min-w-0 flex-1 space-y-1.5"><Skeleton className={clsx('h-3.5', widthAt(r, 0))} /><Skeleton className={clsx('h-3', widthAt(r, 1))} /></div></div>
            : <Skeleton key={c} className={clsx('h-4', widthAt(r, c))} />
        ))}
        {actions && <Skeleton className="h-7 w-7 rounded-lg" />}
      </div>
    ))}
  </div>
);

/** A whole list: toolbar, rows and the pagination line, in one card. */
export const ListSkeleton: React.FC<{ rows?: number; columns?: number; toolbar?: boolean; className?: string }> = ({ rows = 8, columns = 5, toolbar = true, className }) => (
  <SkeletonRegion kind="list" className={clsx(card, 'overflow-hidden', className)}>
    {toolbar && <ListToolbarSkeleton />}
    <TableRowsSkeleton rows={rows} columns={columns} />
    <div className="flex items-center justify-between px-5 py-3">
      <Skeleton className="h-3.5 w-40" />
      <div className="flex gap-1.5">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-8 w-8 rounded-lg" />)}</div>
    </div>
  </SkeletonRegion>
);

/** Lines of a list inside a card that is already drawn: icon, two lines of text, something on the right. */
export const RowsSkeleton: React.FC<{ rows?: number; className?: string; divided?: boolean }> = ({ rows = 4, className, divided = true }) => (
  <SkeletonRegion kind="rows" className={clsx(divided && 'divide-y divide-border', className)}>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3">
        <Skeleton className="h-9 w-9 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 space-y-1.5"><Skeleton className={clsx('h-3.5', widthAt(i, 0))} /><Skeleton className={clsx('h-3', widthAt(i, 1))} /></div>
        <Skeleton className="h-4 w-20" />
      </div>
    ))}
  </SkeletonRegion>
);

/* ── forms ─────────────────────────────────────────────────────────────── */

export const FieldSkeleton: React.FC<{ tall?: boolean; className?: string }> = ({ tall, className }) => (
  <div className={clsx('space-y-2', className)}>
    <Skeleton className="h-3.5 w-24" />
    <Skeleton className={clsx('w-full rounded-lg', tall ? 'h-24' : 'h-10')} />
  </div>
);

export const FieldGridSkeleton: React.FC<{ fields: number }> = ({ fields }) => (
  <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
    {Array.from({ length: fields }, (_, i) => <FieldSkeleton key={i} />)}
  </div>
);

/** A titled card of fields. `fields = 0` draws one tall field (a textarea card). */
export const FormCardSkeleton: React.FC<{ fields?: number; title?: boolean }> = ({ fields = 6, title = true }) => (
  <div className={clsx(card, 'overflow-hidden')}>
    {title && (
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Skeleton className="h-9 w-9 rounded-lg" />
        <div className="space-y-1.5"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-64" /></div>
      </div>
    )}
    <div className="p-5">{fields === 0 ? <FieldSkeleton tall /> : <FieldGridSkeleton fields={fields} />}</div>
  </div>
);

export const FormFooterSkeleton: React.FC<{ buttons?: number }> = ({ buttons = 2 }) => (
  <div className="flex items-center justify-between gap-3">
    <Skeleton className="h-4 w-40" />
    <div className="flex gap-2">{Array.from({ length: buttons }, (_, i) => <Skeleton key={i} className={clsx('h-10 rounded-lg', i === buttons - 1 ? 'w-32' : 'w-24')} />)}</div>
  </div>
);

/** A form of titled cards: `sections` lists each card's number of fields. */
export const FormPageSkeleton: React.FC<{ sections?: number[]; footer?: boolean; className?: string }> = ({ sections = [4, 0, 2], footer = true, className }) => (
  <SkeletonRegion kind="form" className={clsx('space-y-5', className)}>
    {sections.map((n, i) => <FormCardSkeleton key={i} fields={n} />)}
    {footer && <FormFooterSkeleton />}
  </SkeletonRegion>
);

/* ── tiles, panels, records, overviews ────────────────────────────────── */

export const StatTileSkeleton: React.FC = () => (
  <div className={clsx(card, 'p-5')}>
    <div className="flex items-start justify-between"><Skeleton className="h-3 w-20" /><Skeleton className="h-9 w-9 rounded-lg" /></div>
    <Skeleton className="mt-4 h-7 w-24" />
    <Skeleton className="mt-3 h-3 w-28" />
  </div>
);

export const StatTilesSkeleton: React.FC<{ count?: number; className?: string; region?: boolean }> = ({ count = 4, className, region = true }) => {
  const tiles = (
    <div className={clsx('grid gap-4', className)} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
      {Array.from({ length: count }, (_, i) => <StatTileSkeleton key={i} />)}
    </div>
  );
  return region ? <SkeletonRegion kind="tiles">{tiles}</SkeletonRegion> : tiles;
};

const ChartBars: React.FC<{ className?: string }> = ({ className }) => (
  <div className={clsx('flex items-end gap-2', className)}>
    {[45, 70, 35, 85, 60, 75, 40, 90, 55, 65, 50, 80].map((h, i) => <Skeleton key={i} className="flex-1 rounded-b-none" style={{ height: `${h}%` }} />)}
  </div>
);

export const ChartSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <SkeletonRegion kind="chart" className={clsx('flex h-full min-h-[120px] flex-col justify-end p-3', className)}>
    <ChartBars className="h-full min-h-[100px]" />
    <Skeleton className="mt-2 h-px w-full rounded-none" />
  </SkeletonRegion>
);

/** A titled card holding lines — a panel, a timeline, a short list — or a chart. */
export const PanelSkeleton: React.FC<{ lines?: number; title?: boolean; chart?: boolean; className?: string; region?: boolean }> = ({ lines = 4, title = true, chart, className, region }) => {
  const panel = (
    <div className={clsx(card, className)}>
      {title && (
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-3"><Skeleton className="h-9 w-9 rounded-lg" /><div className="space-y-1.5"><Skeleton className="h-4 w-36" /><Skeleton className="h-3 w-52" /></div></div>
          <Skeleton className="h-8 w-20 rounded-lg" />
        </div>
      )}
      <div className="space-y-3 p-5">
        {chart ? <ChartBars className="h-48" /> : Array.from({ length: lines }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-1.5"><Skeleton className={clsx('h-3.5', widthAt(i, 0))} /><Skeleton className={clsx('h-3', widthAt(i, 1))} /></div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
  return region ? <SkeletonRegion kind="panel">{panel}</SkeletonRegion> : panel;
};

/** A record's details: label / value pairs in columns. */
export const DetailCardSkeleton: React.FC<{ rows?: number; title?: boolean }> = ({ rows = 6, title = true }) => (
  <div className={clsx(card, 'p-5')}>
    {title && <Skeleton className="mb-4 h-4 w-32" />}
    <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
      {Array.from({ length: rows }, (_, i) => <div key={i} className="space-y-1.5"><Skeleton className="h-3 w-20" /><Skeleton className={clsx('h-4', widthAt(i, 2))} /></div>)}
    </div>
  </div>
);

/** A record page: a hero strip, optional tiles, then cards of details with an optional side column. */
export const DetailPageSkeleton: React.FC<{ tiles?: number; cards?: number[]; side?: boolean; className?: string }> = ({ tiles = 0, cards = [8, 4], side = false, className }) => (
  <SkeletonRegion kind="detail" className={clsx('space-y-4', className)}>
    <div className={clsx(card, 'flex items-center gap-4 p-5')}>
      <Skeleton className="h-14 w-14 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1 space-y-2"><Skeleton className="h-5 w-56" /><Skeleton className="h-3 w-72 max-w-full" /></div>
      <Skeleton className="h-9 w-24 rounded-lg" />
    </div>
    {tiles > 0 && <StatTilesSkeleton count={tiles} region={false} />}
    <div className={clsx('grid gap-4', side && 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]')}>
      <div className="space-y-4">{cards.map((n, i) => <DetailCardSkeleton key={i} rows={n} />)}</div>
      {side && <PanelSkeleton lines={5} className="self-start" />}
    </div>
  </SkeletonRegion>
);

/** An overview / dashboard: filters, tiles, a chart beside a short list, two more panels. */
export const OverviewSkeleton: React.FC<{ tiles?: number; filters?: boolean; className?: string }> = ({ tiles = 4, filters = true, className }) => (
  <SkeletonRegion kind="overview" className={clsx('space-y-4', className)}>
    {filters && <div className="flex flex-wrap items-center gap-2"><Skeleton className="h-9 w-40 rounded-lg" /><Skeleton className="h-9 w-32 rounded-lg" /><Skeleton className="h-9 w-28 rounded-lg" /></div>}
    <StatTilesSkeleton count={tiles} region={false} />
    <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"><PanelSkeleton chart /><PanelSkeleton lines={5} /></div>
    <div className="grid gap-4 lg:grid-cols-2"><PanelSkeleton lines={4} /><PanelSkeleton lines={4} /></div>
  </SkeletonRegion>
);

/** A grid of cards (templates, accounts, galleries). */
export const CardGridSkeleton: React.FC<{ count?: number; media?: boolean; className?: string }> = ({ count = 6, media, className }) => (
  <SkeletonRegion kind="cards" className={clsx('grid gap-4 sm:grid-cols-2 xl:grid-cols-3', className)}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className={clsx(card, 'overflow-hidden')}>
        {media && <Skeleton className="h-32 w-full rounded-none" />}
        <div className="p-5">
          <div className="flex items-start justify-between"><Skeleton className="h-10 w-10 rounded-xl" /><Skeleton className="h-5 w-16 rounded-full" /></div>
          <Skeleton className="mt-4 h-4 w-2/3" />
          <Skeleton className="mt-2 h-3 w-5/6" />
          <Skeleton className="mt-2 h-3 w-1/2" />
          <div className="mt-5 flex gap-2"><Skeleton className="h-8 w-20 rounded-lg" /><Skeleton className="h-8 w-8 rounded-lg" /></div>
        </div>
      </div>
    ))}
  </SkeletonRegion>
);

/** A settings page: a header, then sections of explanation + a card of fields. */
export const SettingsSkeleton: React.FC<{ sections?: number; className?: string }> = ({ sections = 3, className }) => (
  <SkeletonRegion kind="settings" className={className}>
    <div className="space-y-2 border-b border-border pb-6"><Skeleton className="h-3 w-16" /><Skeleton className="h-7 w-56" /><Skeleton className="h-3.5 w-96 max-w-full" /></div>
    {Array.from({ length: sections }, (_, i) => (
      <div key={i} className="grid gap-5 py-7 md:grid-cols-[minmax(0,280px)_minmax(0,1fr)] md:gap-10">
        <div className="space-y-2"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /></div>
        <FormCardSkeleton fields={i === 1 ? 2 : 4} title={false} />
      </div>
    ))}
  </SkeletonRegion>
);

/* ── conversations ────────────────────────────────────────────────────── */

export const ConversationListSkeleton: React.FC<{ rows?: number; className?: string }> = ({ rows = 8, className }) => (
  <SkeletonRegion kind="conversations" className={className}>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3">
        <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex justify-between gap-3"><Skeleton className={clsx('h-3.5 max-w-[60%]', widthAt(i, 0))} /><Skeleton className="h-3 w-10" /></div>
          <Skeleton className={clsx('h-3', widthAt(i, 2))} />
        </div>
      </div>
    ))}
  </SkeletonRegion>
);

export const MessagesSkeleton: React.FC<{ count?: number; className?: string }> = ({ count = 7, className }) => (
  <SkeletonRegion kind="messages" className={clsx('flex flex-col gap-3 px-4 py-6', className)}>
    {Array.from({ length: count }, (_, i) => {
      const mine = i % 3 === 1;
      return (
        <div key={i} className={clsx('flex', mine ? 'justify-end' : 'justify-start')}>
          <Skeleton className={clsx('rounded-2xl', mine ? 'rounded-br-sm' : 'rounded-bl-sm', ['h-10 w-56', 'h-14 w-72', 'h-10 w-40', 'h-16 w-64'][i % 4])} />
        </div>
      );
    })}
  </SkeletonRegion>
);

export const ChipsSkeleton: React.FC<{ count?: number; className?: string }> = ({ count = 8, className }) => (
  <SkeletonRegion kind="chips" className={clsx('flex flex-wrap gap-2', className)}>
    {Array.from({ length: count }, (_, i) => <Skeleton key={i} className={clsx('h-9 rounded-lg', i % 3 ? 'w-20' : 'w-24')} />)}
  </SkeletonRegion>
);

export const DialogFormSkeleton: React.FC<{ fields?: number; columns?: 1 | 2; className?: string }> = ({ fields = 4, columns = 2, className }) => (
  <SkeletonRegion kind="dialog-form" className={clsx('grid gap-4 py-2', columns === 2 && 'sm:grid-cols-2', className)}>
    {Array.from({ length: fields }, (_, i) => <FieldSkeleton key={i} />)}
  </SkeletonRegion>
);

/* ── whole pages, for a screen whose shell is still loading ───────────── */

/** The page header (Topbar): crumb, title, subtitle and the right-hand actions. */
export const PageHeaderSkeleton: React.FC<{ actions?: number }> = ({ actions = 2 }) => (
  <div className="flex h-[72px] items-center justify-between border-b border-border bg-surface px-4 sm:px-7">
    <div className="space-y-2">
      <Skeleton className="h-2.5 w-28" />
      <Skeleton className="h-5 w-44" />
    </div>
    <div className="hidden items-center gap-2 sm:flex">
      {Array.from({ length: actions }, (_, i) => <Skeleton key={i} className={clsx('h-9 rounded-lg', i === actions - 1 ? 'w-32' : 'w-24')} />)}
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>
  </div>
);

/** What kind of screen a path shows, from its shape. */
export function screenKindOf(pathname: string): 'form' | 'detail' | 'overview' | 'cards' | 'list' | 'chat' {
  const p = pathname.toLowerCase().replace(/\/+$/, '');
  const last = p.split('/').pop() ?? '';
  if (p === '/chats' || p.startsWith('/chats/')) return 'chat';
  if (/\/(new|create|add|edit|workspace|builder)(\/|$)/.test(p) || last.startsWith('edit') || last.startsWith('new')) return 'form';
  if (/^\/(dashboard|whatsapp|tiktok|instagram|reports)$/.test(p) || /(overview|dashboard|insights|analytics|usage)$/.test(last)) return 'overview';
  if (/^\/settings(\/|$)/.test(p) || /(accounts|catalog|tools|apps)$/.test(last)) return 'cards';
  if (/^[0-9a-z_-]{10,}$/i.test(last) && /\d|[A-Z]/.test(last) && !/^[a-z-]+$/.test(last)) return 'detail';
  return 'list';
}

export const ScreenSkeleton: React.FC<{ kind: ReturnType<typeof screenKindOf> }> = ({ kind }) =>
  kind === 'form' ? <FormPageSkeleton />
    : kind === 'detail' ? <DetailPageSkeleton tiles={4} cards={[8, 4]} side />
    : kind === 'overview' ? <OverviewSkeleton />
    : kind === 'cards' ? <CardGridSkeleton count={6} />
    : kind === 'chat' ? (
      <div className="grid h-[calc(100vh-140px)] grid-cols-[320px_minmax(0,1fr)] overflow-hidden rounded-2xl border border-border bg-surface">
        <div className="border-r border-border"><div className="border-b border-border p-3"><Skeleton className="h-9 w-full rounded-lg" /></div><ConversationListSkeleton rows={9} /></div>
        <div className="flex flex-col"><div className="flex items-center gap-3 border-b border-border px-4 py-3"><Skeleton className="h-9 w-9 rounded-full" /><div className="space-y-1.5"><Skeleton className="h-3.5 w-40" /><Skeleton className="h-3 w-24" /></div></div><MessagesSkeleton count={8} className="flex-1" /></div>
      </div>
    )
    : <ListSkeleton />;

/** A whole page — header and body — shaped after the path it is for. */
export const RouteSkeleton: React.FC<{ pathname?: string }> = ({ pathname }) => {
  const loc = useLocation();
  const kind = screenKindOf(pathname ?? loc.pathname);
  return (
    <div data-route-skeleton={kind}>
      <PageHeaderSkeleton />
      <div className="px-4 py-6 sm:px-7"><ScreenSkeleton kind={kind} /></div>
    </div>
  );
};
